import React, {
  Dispatch,
  SetStateAction,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../componentLibrary/Modal";
import Button from "../componentLibrary/Button";
import TutorialTip from "../componentLibrary/TutorialTip";

interface ProgressProps {
  steps: string[];
  currentStep: number;
  className?: string;
  onClick?: Dispatch<SetStateAction<number>>;
  centered?: boolean;
  moduleId?: string;
  moduleTitle?: string;
  moduleIcon?: {
    src: string;
    alt: string;
  };
  lessonTitle?: string;
  // New props for restart functionality
  onRestart?: (type: "keep" | "save" | "clear") => void;
  hasAnswers?: boolean;
  showTutorialTip?: boolean;
}

export interface ProgressRef {
  openModal: () => void;
}

const Progress = forwardRef<ProgressRef, ProgressProps>(
  (
    {
      steps,
      currentStep,
      className,
      onClick,
      centered = false,
      moduleId,
      moduleTitle,
      lessonTitle,
      onRestart,
      hasAnswers = false,
      showTutorialTip = true,
    },
    ref,
  ) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showRestartOptions, setShowRestartOptions] = useState(false);
    const navigate = useNavigate();

    useImperativeHandle(ref, () => ({
      openModal: () => setIsModalOpen(true),
    }));

    const handleProgressClick = () => {
      setIsModalOpen(true);
      setShowRestartOptions(false);
    };

    const handlePageClick = (index: number) => {
      onClick?.(index);
      setIsModalOpen(false);
      setShowRestartOptions(false);
    };

    const handleModuleClick = () => {
      if (moduleId) {
        navigate(`/topics/${moduleId}`);
        setIsModalOpen(false);
        setShowRestartOptions(false);
      }
    };

    const handleRestartClick = () => {
      setShowRestartOptions(true);
    };

    const handleRestartOption = (type: "keep" | "save" | "clear") => {
      onRestart?.(type);
      setIsModalOpen(false);
      setShowRestartOptions(false);
    };

    const handleBackToPages = () => {
      setShowRestartOptions(false);
    };

    const progressButton = (
      <Button
        variant="secondary"
        size="sm"
        onClick={handleProgressClick}
        className="text-sm font-medium"
        data-progress-button
        roundedFull
      >
        Page {currentStep + 1}/{steps.length}
      </Button>
    );

    return (
      <>
        <div className={className ? `relative ${className}` : "relative"}>
          <div
            style={{
              textAlign: centered ? "center" : "left",
            }}
          >
            {showTutorialTip ? (
              <TutorialTip
                direction="top"
                tutorialId={"progress-button-tutorial"}
                maxWidth={280}
                content={
                  <div className="text-sm">
                    <p className="font-medium mb-2">
                      View the Table of Contents
                    </p>
                    <p>Click here to see all pages in this section.</p>
                  </div>
                }
              >
                {progressButton}
              </TutorialTip>
            ) : (
              progressButton
            )}
          </div>
        </div>

        <Modal
          isOpen={isModalOpen}
          onCloseModal={() => {
            setIsModalOpen(false);
            setShowRestartOptions(false);
          }}
          title={
            showRestartOptions
              ? `Restart ${lessonTitle || "Lesson"}`
              : lessonTitle || "Table of Contents"
          }
          size="lg"
          titleCentered
          backgroundColor="bg-gray-100"
        >
          {showRestartOptions ? (
            <div className="space-y-3">
              <div className="text-center mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Choose what to do with your current answers:
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleRestartOption("keep")}
                className="text-sm font-medium w-full"
                roundedFull
              >
                <strong>{"Keep\u00A0"}</strong>Current Answers Filled-in
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleRestartOption("save")}
                className="text-sm font-medium w-full"
                roundedFull
              >
                <strong>{"Save\u00A0"}</strong>Current Answers to My Log
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleRestartOption("clear")}
                className="text-sm font-medium w-full"
                roundedFull
              >
                <strong>{"Clear\u00A0"}</strong>Current Answers
              </Button>
              <div className="pt-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleBackToPages}
                  className="text-sm font-medium w-full"
                  roundedFull
                >
                  Back to Page Menu
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-y-auto space-y-3 pb-4 bg-white rounded-lg shadow-sm mx-0 md:mx-2 p-4">
                {steps.map((step, index) => (
                  <Button
                    key={index}
                    variant={index === currentStep ? "primary" : "secondary"}
                    size="sm"
                    onClick={() => handlePageClick(index)}
                    className="w-full text-left justify-start my-1 py-6"
                  >
                    <span className="text-sm">{step}</span>
                  </Button>
                ))}
              </div>

              {(moduleId && moduleTitle) || hasAnswers ? (
                <div className="pt-4 mt-4">
                  <div className="space-y-3 px-2">
                    {moduleId && moduleTitle && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleModuleClick}
                        className="text-sm font-medium w-full py-4 min-h-[60px] flex items-center"
                        roundedFull
                      >
                        <span className="text-center w-full">
                          {"Return to Menu for\u00A0"}
                          <strong>{moduleTitle}</strong>
                        </span>
                      </Button>
                    )}
                    {hasAnswers && (
                      <Button
                        variant="dark"
                        size="sm"
                        onClick={handleRestartClick}
                        className="text-sm font-medium w-full py-4 min-h-[60px] flex items-center"
                        roundedFull
                      >
                        <span className="text-center w-full">
                          {"Restart\u00A0"}
                          <strong>{lessonTitle || "lesson"}</strong>
                        </span>
                      </Button>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </Modal>
      </>
    );
  },
);

Progress.displayName = "Progress";

export default Progress;
